import * as React from "react";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { useNavigate } from "react-router-dom";
import { AddRounded, DescriptionRounded, DownloadRounded } from "@mui/icons-material";
import { Button, Stack, useTheme } from "@mui/material";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import { useRoyaltiesReportRepository } from "../../../api/repositories/RoyaltiesReportRepository";
import { STATE_DISABLE_TAB, STATE_REPORT_ACTIVE_FILTERS, STATE_REPORT_TOTAL_COUNT, STATE_REPORT_UPDATING } from "../constants/RoyaltiesRecoil";
import { formatDate } from "../../../utils/FormatUtils";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { downloadJSONAsXLSX } from "../../../utils/ExcelUtility";
import { processReportJson } from "./ReportTableUtil";
import { useRoyaltiesPrintoutRepository } from "../../../api/repositories/RoyaltiesPrintoutRepository";
import { useAgGridLocale, useHasScope } from "../../../../../config/globalConfig";
import { CREATE_PRINTOUT, DOWNLOAD_REPORT, VIEW_REPORT_DETAIL } from "../../../utils/FinanceScope";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

const ReportTable = () => {

    const UNIQUE_ID = "fnn-royalties-report";

    const { t } = useTranslation('finance', { keyPrefix: 'royalties' });

    const { getReports } = useRoyaltiesReportRepository();
    const { getPrintoutsByReportId } = useRoyaltiesPrintoutRepository();
    const hasScope = useHasScope;
    const [disableTab, setDisableTab] = useRecoilState(STATE_DISABLE_TAB);
    const resetDisableTab = useResetRecoilState(STATE_DISABLE_TAB);
    resetDisableTab();
    const [updating, setUpdating] = useRecoilState(STATE_REPORT_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const setTotalDataCount = useSetRecoilState(STATE_REPORT_TOTAL_COUNT);

    const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);
    const agGridLocale = useAgGridLocale();

    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);

    const navigate = useNavigate();
    
    const {  globalData: { user } } = useContext(GlobalContext);
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");
    
    const columnDefs : ColDef[] = [
        {
            field: 'name',
            headerName: t('tables.report.report-name'),
            headerTooltip: t('tables.report.report-name'),
            unSortIcon: true,
        },
        {
            field: 'created',
            headerName: t('tables.report.report-creation-date'),
            headerTooltip: t('tables.report.report-creation-date'),
            unSortIcon: true,
            cellRenderer: (params: any) => formatDate(params.value),
        },
        {
            field: 'printoutCount',
            headerName: t('tables.report.printouts-count'),
            headerTooltip: t('tables.report.printouts-count'),
            unSortIcon: true
        },
        {
            field: "legalEntity", headerName: t("tables.report.legal-entity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                            <Stack direction={"row"} justifyContent={"space-between"}>
                                {hasScope(VIEW_REPORT_DETAIL) &&
                                <IconButton sx={{ color: theme.palette.accent.main }} onClick={() => {
                                    setDisableTab(true);
                                    window.open('/finance/royalties/report/' + params.data.id, "_blank");
                                }}>
                                    <DescriptionRounded/>
                                </IconButton>
                                }
                                {hasScope(DOWNLOAD_REPORT) &&
                                <IconButton sx={{ color: theme.palette.accent.main }} onClick={() => {
                                    getPrintoutsByReportId(params.data.id).then(it => {
                                        console.log(it.result.content);
                                        downloadJSONAsXLSX(params.data.name, processReportJson(it.result.content));
                                    })
                                }}>
                                    <DownloadRounded/>
                                </IconButton>
                                }
                            </Stack>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t("tables.report.no-rows.title"),
            content: t("tables.report.no-rows.content"),
        }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getReports(startRow, endRow - startRow, sort, order, filterDefs)
                        .then(response => {
                            let result = response.result;
                            setTotalDataCount(result.page.totalElements);
                            if (result.page.totalElements === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result.page.totalElements
                            })
                        })
                        .catch(() => params.fail());
            }
        };
    }


    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (<></>
        ),
        renderActions: () => (
                <Stack>
                    {hasScope(CREATE_PRINTOUT) &&
                    <Button fullWidth color={"accent"} variant="contained" component="label" onClick={() => {
                        navigate("/finance/royalties/printouts-wizard")
                    }} startIcon={<AddRounded/>}>{t('buttons.add-printout')}</Button>
                    }
                </Stack>
        )
    }

    return (
            <AgGridCustomTable
                    toolbar={toolbar}
                    rowModelType='serverSide'
                    paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                    gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                    rowSelection={'multiple'}
                    noRowsOverlayComponent={noRowsOverlayComponent}
                    suppressRowClickSelection
                    onGridReady={() => {
                        gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                        let gridState = getLocalGridState(UNIQUE_ID).cols;
                        if (gridState !== undefined) {
                            gridRef.current!.columnApi.applyColumnState({
                                state: gridState,
                                applyOrder: true
                            })
                        }
                    }}
                    onColumnMoved={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onColumnPinned={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onColumnResized={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onSortChanged={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onPaginationChanged={() => {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols
                        });
                    }}
            />
    );
}

export default ReportTable;